// Audit log: every target-affecting / money-affecting change in this module —
// who, what, for whom, before → after, why, from which IP.
import { useState } from 'react';
import { ScrollText } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { monthName, useAudit } from '../api';
import { Chip, Empty, Panel, SelectPill } from './ui';

const ACTION_LABEL: Record<string, string> = {
  target_changed: 'Target set / changed', team_profile_changed: 'Team setup changed',
  rules_changed: 'Incentive rules changed', incentives_calculated: 'Incentives calculated', incentive_adjusted: 'Incentive adjusted',
  month_closed: 'Month closed', month_reopened: 'Month reopened', incentive_submit: 'Sent for review', incentive_manager_approve: 'Manager approved',
  incentive_finance_approve: 'Finance verified', incentive_approve: 'Final approval', incentive_mark_paid: 'Marked paid',
  incentive_reject: 'Rejected', incentive_hold: 'Put on hold',
};
const TONE: Record<string, string> = {
  target_changed: 'bg-blue-50 text-blue-700', team_profile_changed: 'bg-violet-50 text-violet-700', rules_changed: 'bg-amber-50 text-amber-700',
  incentive_mark_paid: 'bg-emerald-50 text-emerald-700', incentive_reject: 'bg-rose-50 text-rose-700', incentive_adjusted: 'bg-amber-50 text-amber-700',
};

/** Shows a before/after object as "key: value" pairs, only the keys that changed. */
function diff(before: unknown, after: unknown) {
  const b = (before && typeof before === 'object' ? before : {}) as Record<string, unknown>;
  const a = (after && typeof after === 'object' ? after : {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k]));
  const show = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v));
  return keys.slice(0, 6).map((k) => ({ k, from: show(b[k]), to: show(a[k]) }));
}

export function AuditPanel() {
  const { data, isLoading } = useAudit(true);
  const [action, setAction] = useState('');
  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (!data?.length) return <Empty title="Nothing recorded yet" text="Target changes, rule changes, calculations, approvals and payments will appear here." />;
  const list = action ? data.filter((a) => a.action === action) : data;
  const actions = [...new Set(data.map((a) => a.action))];
  return (
    <Panel pad={false} title="Audit log" subtitle="Every change to targets, rules, team setup and incentives" icon={ScrollText}
      right={<SelectPill label="Show" value={action} onChange={setAction} allLabel="Everything" options={actions.map((a) => ({ value: a, label: ACTION_LABEL[a] || a }))} />}>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr className="bg-slate-50 text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500">
            {['When', 'Who', 'What', 'Employee', 'Month', 'Change', 'Reason'].map((h) => <th key={h} className="whitespace-nowrap px-4 py-2.5">{h}</th>)}
          </tr></thead>
          <tbody>
            {list.map((a) => {
              const d = diff(a.before, a.after);
              return (
                <tr key={a._id} className="border-t border-slate-100 align-top">
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">{new Date(a.at).toLocaleString('en-IN')}<div className="text-data-xs text-slate-400">{a.ip}</div></td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{a.actorName}</td>
                  <td className="px-4 py-2.5"><Chip className={TONE[a.action] || 'bg-slate-100 text-slate-700'}>{ACTION_LABEL[a.action] || a.action}</Chip></td>
                  <td className="px-4 py-2.5 text-slate-700">{a.employeeName || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">{a.periodKey ? monthName(a.periodKey) || a.periodKey : '—'}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {d.length ? d.map((x) => <div key={x.k}><span className="text-slate-500">{x.k}:</span> <span className="text-slate-400 line-through">{x.from}</span> → <b className="text-slate-800">{x.to}</b></div>) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="max-w-xs px-4 py-2.5 text-xs text-slate-600">{a.reason || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
